/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * CustomTabContainer Component
 *
 * Acts as a wrapper for CustomTab components.
 * Handles horizontal scroll and shows static left/right navigation buttons when overflow exists.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {number|string} props.activeTab - Currently active tab id.
 * @param {Function} props.setActiveTab - Function to update active tab.
 * @param {React.ReactNode} props.children - CustomTab elements.
 * @param {string} props.className - Additional class names for the container (optional).
 * @param {string} props.classNameActions - Additional class names for the action button (optional).
 * @param {string} props.classNameTabs - Additional class names for the tab strip (optional).
 * @param {boolean} props.emptyFields - Indicates that the form was validated, so the tab strip is highlighted and the required message is rendered when no tab is selected (optional).
 * @param {string} props.buttonAction - Additional button (optional).
 *
 * @returns {JSX.Element} Rendered CustomTabContainer.
 */

import React, {useEffect, useRef, useState} from "react";
import { FormHelperText } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BackArrowIcon, NextArrowIcon } from "./buttons/IconButtons";
import { CustomStyleFormHelperText } from "./Styles";

const CustomTabContainer = ({ activeTab, setActiveTab, children, className,classNameActions, classNameTabs, emptyFields = false, buttonAction }) => {
    const { t } = useTranslation();
    const tabsRef = useRef(null);
    const [showScrollButtons, setShowScrollButtons] = useState(false);

    const scrollTabs = (direction) => {
        if (tabsRef.current) {
            const scrollAmount = 200;
            tabsRef.current.scrollBy({
                left: direction === "right" ? scrollAmount : -scrollAmount,
                behavior: "smooth",
            });
        }
    };

    /**
     * Checks whether the tab container content overflows horizontally
     * and determines if scroll buttons should be displayed.
     *
     * - Compares `scrollWidth` (total width of the scrollable content)
     *   with `clientWidth` (visible width of the container).
     * - If content exceeds visible width, `setShowScrollButtons(true)` is called.
     */
    const checkScrollStatus = () => {
        if (!tabsRef.current) return;
        const { scrollWidth, clientWidth } = tabsRef.current;

        setShowScrollButtons(scrollWidth > clientWidth);
    };

    /**
     * useEffect hook to monitor tab container size and scroll changes.
     *
     * - Runs once on mount.
     * - Adds listeners for `scroll` (on the tab container)
     *   and `resize` (on the window) to dynamically update scroll button visibility.
     * - Cleans up event listeners on unmount to prevent memory leaks.
     */
    useEffect(() => {
        const tabs = tabsRef.current;
        if (!tabs) return;

        checkScrollStatus();

        tabs.addEventListener("scroll", checkScrollStatus);
        window.addEventListener("resize", checkScrollStatus);

        return () => {
            tabs.removeEventListener("scroll", checkScrollStatus);
            window.removeEventListener("resize", checkScrollStatus);
        };
    }, []);

    const childrenWithProps = Array.isArray(children)
        ? children.map((child) =>
            child.type?.displayName === "CustomTab"
                ? {
                    ...child,
                    props: {
                        ...child.props,
                        isSelected: activeTab === child.props.id,
                        onClick: activeTab === child.props.id
                            ? undefined
                            : () => setActiveTab(child.props.id)
                    },
                }
                : child
        )
        : children;

    // A tab has to be selected, so the strip behaves like a required field.
    const tabRequired = emptyFields && !activeTab;

    const sizingClass = buttonAction ? "flex-shrink-1 minw-0" : "flex-grow-1";

    /**
     * Tab strip of the container.
     *
     * Extracted so it can be wrapped in a positioned column when a helper text has
     * to be anchored right below the tab strip border without breaking the row
     * that holds the action of the container.
     */
    const tabsStrip = (
        <div
            ref={tabsRef}
            className={`d-flex gap-1 overflow-auto ${tabRequired ? "" : sizingClass} p-1 border-radius-8px scroll-smooth-hidden align-items-center hide-scrollbar background-shades-100 ${tabRequired ? "border-full-error-code-600" : "border-full-transparent"} ${classNameTabs ?? ""}`}
        >
            {childrenWithProps}
        </div>
    );

    return (
        <div className={`d-flex align-items-center border ${className} py-2 gap-1 position-relative `} >
            {/* overlay */}
            {showScrollButtons && (
                <button
                    id="btn_ctc_scroll_tabs_left"
                    className="d-flex justify-content-center align-items-center rounded-circle border border-shades-200 bg-general-00 p-1"
                    onClick={() => scrollTabs("left")}
                >
                    <BackArrowIcon className="h-24px w-24px" />
                </button>
            )}

            { tabRequired
                ? <div className={`d-flex flex-column position-relative ${sizingClass}`}>
                    {tabsStrip}
                    <FormHelperText error={true} sx={CustomStyleFormHelperText(false, false)}>
                        {t("requiredField")}
                    </FormHelperText>
                </div>
                : tabsStrip
            }
            {buttonAction && <div className={classNameActions}>{buttonAction}</div>}
            {showScrollButtons && (
                <button
                    id="btn_ctc_scroll_tabs_right"
                    className="d-flex justify-content-center align-items-center rounded-circle border border-shades-200 bg-general-00 p-1"
                    onClick={() => scrollTabs("right")}
                >
                    <NextArrowIcon className="h-24px w-24px" />
                </button>
            )}
        </div>
    );
};

export default CustomTabContainer;
